import React from 'react';
import { Box, Button, Flex, Text } from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from './AdminIcon';

// Keep the first, last, and nearby pages reachable without a long row of buttons.
function visiblePages(page, totalPages) {
  const pages = totalPages <= 7
    ? Array.from({ length: totalPages }, (_, index) => index + 1)
    : [...new Set([1, totalPages, ...Array.from({ length: 5 }, (_, index) =>
      Math.min(Math.max(page - 2, 2), totalPages - 5) + index)])].sort((a, b) => a - b);
  return pages.flatMap((value, index) => index && value - pages[index - 1] > 1
    ? [`gap-${value}`, value] : [value]);
}

export default function AdminPagination({
  page, totalPages, totalItems, pageSize, isLoading = false,
  onPageChange, itemLabel = 'records', ...props
}) {
  if (totalPages <= 1) return null;

  const changePage = (nextPage) => {
    if (!isLoading && nextPage !== page && nextPage >= 1 && nextPage <= totalPages) {
      onPageChange(nextPage);
    }
  };
  const count = Number.isFinite(totalItems) && totalItems > 0
    ? (pageSize
      ? `${((page - 1) * pageSize + 1).toLocaleString()}–${Math.min(page * pageSize, totalItems).toLocaleString()} of ${totalItems.toLocaleString()} ${itemLabel}`
      : `${totalItems.toLocaleString()} ${itemLabel}`)
    : `Page ${page} of ${totalPages}`;

  return (
    <Flex as="nav" aria-label="Pagination" className="admin-pagination"
      align="center" justify="space-between" gap={3} flexWrap="wrap"
      width="100%" mt={4} pt={4} borderTop="1px solid" borderColor="gray.200"
      sx={{ fontVariantNumeric: 'tabular-nums' }} {...props}>
      <Text fontSize="sm" color="gray.600" aria-live="polite"
        width={{ base: '100%', md: 'auto' }} textAlign={{ base: 'center', md: 'left' }}>
        {count}
      </Text>
      <Flex align="center" justify="center" gap={1} width={{ base: '100%', md: 'auto' }}>
        <Button variant="outline" size="md" px={3} leftIcon={<ChevronLeftIcon />}
          aria-label="Previous page" onClick={() => changePage(page - 1)}
          isDisabled={page <= 1 || isLoading}>Previous</Button>
        <Text display={{ base: 'block', md: 'none' }} fontSize="sm" px={2} whiteSpace="nowrap">
          {page} of {totalPages}
        </Text>
        <Flex display={{ base: 'none', md: 'flex' }} gap={1}>
          {visiblePages(page, totalPages).map(value => typeof value === 'number'
            ? <Button key={value} variant={value === page ? 'solid' : 'ghost'}
                colorScheme="gray" size="md" minW={10} px={2}
                aria-label={`Page ${value}`} aria-current={value === page ? 'page' : undefined}
                isDisabled={isLoading} onClick={() => changePage(value)}>{value}</Button>
            : <Box key={value} as="span" alignSelf="center" px={1} color="gray.600" aria-hidden="true">…</Box>)}
        </Flex>
        <Button variant="outline" size="md" px={3} rightIcon={<ChevronRightIcon />}
          aria-label="Next page" onClick={() => changePage(page + 1)}
          isDisabled={page >= totalPages || isLoading}>Next</Button>
      </Flex>
    </Flex>
  );
}
